import { useMemo, useState } from "react";
import { Link, Navigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";

import { INTEGRATOR_MODULE_DEFINITIONS, INTEGRATOR_MODULE_KEYS_HIDDEN_FROM_APP_SWITCHER } from "@hub-modules";
import { APPS } from "@/components/AppSwitcher";
import { Button } from "@/components/ui/Button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Label, TextArea } from "@/components/ui/Input";
import { apiFetch, ApiError } from "@/lib/api";
import { useIntegratorMe } from "@/lib/me-query";
import { firstVisibleAppPath, appsVisibleInAppSwitcher, hubModuleHasAnyAccess } from "@/lib/subapp-access";
import { cn } from "@/lib/utils";

function pathMatchesModule(attemptedPath: string, moduleKey: string): boolean {
  const app = APPS.find((entry) => entry.key === moduleKey);
  if (!app) {
    return false;
  }
  return attemptedPath === app.path || attemptedPath.startsWith(`${app.path}/`);
}

const linkSecondaryClassName =
  "inline-flex h-10 items-center justify-center rounded-md border border-slate-300 bg-white px-4 text-sm font-medium text-slate-900 hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2";

export function AccessNeededPage() {
  const [searchParams] = useSearchParams();
  const { data: me, isLoading: meLoading } = useIntegratorMe();
  const [reason, setReason] = useState("");

  const moduleKey = searchParams.get("module")?.trim() ?? "";
  const targetRaw = searchParams.get("target") ?? "";
  const noModules = searchParams.get("nomodules") === "1";

  const target = useMemo(() => {
    try {
      const decoded = decodeURIComponent(targetRaw.trim());
      if (!decoded.startsWith("/") || decoded.includes("..")) {
        return null;
      }
      return decoded.length > 2048 ? null : decoded;
    } catch {
      return null;
    }
  }, [targetRaw]);

  const moduleApp = APPS.find((app) => app.key === moduleKey);
  const paramsOk =
    moduleApp != null && target != null && pathMatchesModule(target, moduleKey);

  const visible = appsVisibleInAppSwitcher(me);
  const homePath = visible.length > 0 ? firstVisibleAppPath(me) : "/";
  const homeApp = visible.length > 0 ? APPS.find((app) => app.path === homePath) : null;

  const submitMutation = useMutation({
    mutationFn: () => {
      if (target == null) {
        return Promise.reject(new Error("Missing target path."));
      }
      return apiFetch<{ ok: boolean; emailSent: boolean; notifyTo: string }>("/api/access-request", {
        method: "POST",
        body: {
          moduleKey,
          attemptedPath: target,
          reason: reason.trim(),
        },
      });
    },
  });

  const { data: integratorCatalog } = useQuery({
    queryKey: ["integrator-modules"],
    queryFn: () =>
      apiFetch<{
        modules: { key: string; label: string; path: string; description: string }[];
      }>("/api/integrator-modules"),
    staleTime: 60 * 60_000,
  });

  const hubModulesSorted = useMemo(() => {
    const rows = integratorCatalog?.modules ?? [...INTEGRATOR_MODULE_DEFINITIONS];
    return [...rows]
      .filter((row) => !INTEGRATOR_MODULE_KEYS_HIDDEN_FROM_APP_SWITCHER.has(row.key))
      .sort((left, right) =>
      left.label.localeCompare(right.label, undefined, { sensitivity: "base" }),
    );
  }, [integratorCatalog?.modules]);

  if (!paramsOk) {
    return <Navigate to="/" replace />;
  }

  if (!meLoading && !me?.user) {
    return <Navigate to="/sign-in" replace />;
  }

  if (meLoading || !me?.user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <p className="text-sm text-slate-600">Loading…</p>
      </div>
    );
  }

  if (hubModuleHasAnyAccess(me, moduleKey)) {
    return <Navigate to={target} replace />;
  }

  const submitted = submitMutation.isSuccess && submitMutation.data?.ok === true;

  return (
    <div className="min-h-screen bg-slate-50 px-4 py-10">
      <div className="mx-auto max-w-3xl space-y-6">
        <header>
          <h1 className="text-xl font-semibold text-slate-900">Module access needed</h1>
          <p className="mt-2 text-sm text-slate-600">
            You opened a link to <span className="font-mono text-xs text-slate-800">{target}</span>, which is
            part of <span className="font-medium text-slate-900">{moduleApp.label}</span>. Your account does
            not currently include that module
            {noModules ? " (you also have no modules enabled yet)" : ""}.
          </p>
        </header>

        {visible.length > 0 ? (
          <p className="text-sm text-slate-600">
            You can continue to{" "}
            <Link className="font-medium text-brand-700 underline hover:text-brand-900" to={homePath}>
              {homeApp?.label ?? "an allowed module"}
            </Link>{" "}
            or send a short request below.
          </p>
        ) : (
          <p className="text-sm text-slate-600">
            You do not have any modules enabled yet. After access is granted, use your bookmark again or open
            the hub from the app switcher.
          </p>
        )}

        <Card>
          <CardHeader>
            <CardTitle>Request access</CardTitle>
          </CardHeader>
          <CardBody className="space-y-4">
            {submitted ? (
              <div className="space-y-2 text-sm text-slate-700">
                <p className="font-medium text-emerald-800">Request sent.</p>
                {submitMutation.data?.emailSent ? (
                  <p>
                    Your message was emailed to the integrator team. You may receive a reply at{" "}
                    <span className="font-mono text-xs">{me.user.email}</span>.
                  </p>
                ) : (
                  <p>
                    Email delivery is not configured on this server, but your request was logged for an
                    administrator. You can also email{" "}
                    <a
                      className="font-medium text-brand-700 underline"
                      href={`mailto:${submitMutation.data?.notifyTo ?? "e.edwards@smartgridsolutions.com"}`}
                    >
                      {submitMutation.data?.notifyTo ?? "e.edwards@smartgridsolutions.com"}
                    </a>{" "}
                    with the link you tried to open.
                  </p>
                )}
                <Link className={cn(linkSecondaryClassName, "mt-2 inline-flex")} to={homePath}>
                  {visible.length > 0 ? `Go to ${homeApp?.label ?? "hub"}` : "Return to home"}
                </Link>
              </div>
            ) : (
              <>
                <div>
                  <Label htmlFor="access-reason">Why do you need access?</Label>
                  <TextArea
                    id="access-reason"
                    className="mt-1.5 font-sans"
                    value={reason}
                    onChange={(event) => setReason(event.target.value)}
                    placeholder="For example: I need to approve a bill of materials rule for work order 12345."
                    maxLength={2000}
                    disabled={submitMutation.isPending}
                  />
                  <p className="mt-1 text-xs text-slate-500">Between 3 and 2000 characters.</p>
                </div>
                {submitMutation.isError ? (
                  <p className="text-sm text-rose-600">
                    {submitMutation.error instanceof ApiError
                      ? submitMutation.error.message
                      : "Could not send request. Try again."}
                  </p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    disabled={submitMutation.isPending || reason.trim().length < 3}
                    onClick={() => {
                      submitMutation.mutate();
                    }}
                  >
                    {submitMutation.isPending ? "Sending…" : "Send request"}
                  </Button>
                  <Link className={linkSecondaryClassName} to={homePath}>
                    {visible.length > 0 ? `Skip — go to ${homeApp?.label ?? "hub"}` : "Return to home"}
                  </Link>
                </div>
              </>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Where hub deep links live</CardTitle>
            <p className="mt-1 text-sm font-normal text-slate-500">
              Each module owns every URL under its path prefix. This list comes from{" "}
              <span className="font-mono text-xs">GET /api/integrator-modules</span> (with the same data as{" "}
              <span className="font-mono text-xs">src/integrator-modules.ts</span>
              ). Adding a module there updates the app switcher, access matrix, and this page together.
            </p>
          </CardHeader>
          <CardBody className="pt-0">
            <div className="overflow-x-auto rounded-md border border-slate-200">
              <table className="w-full min-w-[640px] border-collapse text-left text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
                    <th className="px-3 py-2">Module</th>
                    <th className="px-3 py-2">Deep link root</th>
                    <th className="px-3 py-2">What is addressable here</th>
                  </tr>
                </thead>
                <tbody>
                  {hubModulesSorted.map((row) => (
                    <tr key={row.key} className="border-b border-slate-100 last:border-0">
                      <td className="px-3 py-2 font-medium text-slate-900">{row.label}</td>
                      <td className="px-3 py-2 font-mono text-xs text-slate-800">{row.path}</td>
                      <td className="px-3 py-2 text-slate-600">{row.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardBody>
        </Card>
      </div>
    </div>
  );
}
